import { call, put, race, take, select } from 'redux-saga/effects';
import {
    MissingAuthCookieError,
    TokenClient,
    TokenSession
} from 'urbnTokenLib';

import { RECEIVE_PROFILE } from '../actions/initialActions';
import { LOGIN_FINISHED, LOGIN_CANCELED, userLoginHandled } from '../actions/userActions';

import { init as loginInit } from '../components/loginComponent';

import { needsLogin } from '../utils/loginUtils';
import { launchModal } from '../utils/modalUtils';
import { debug, error, info, warn } from '../utils/logger';

export function* checkLogin(route) {
    let state = yield select();
    const tokenClient = yield call(createTokenClient, state.core.constants.siteId, state.core.constants.urls.token);

    const authVars = {
        hasTokenCookie: false,
        isGuestToken: false,
        showLoginPrompt: false,
        tokenSession: null
    };

    yield call(handleAuth, tokenClient, authVars, route, TokenSession);
    yield call(handleLoginRequired, tokenClient, authVars, route);
}

/**
 * A function that returns a new tokenClient
 * @param {String} siteId The siteId
 * @param {String} tokenBaseURL The tokenBaseURL
 * @return {object} token client
 */
export function createTokenClient(siteId, tokenBaseURL) {
    if (!siteId || !tokenBaseURL) {
        error('Cannot determine site id and token URL in state');
    }

    return new TokenClient(siteId, tokenBaseURL);
}

/**
 * Given the redux state, create a new TokenClient and perform a reauth
 * @return {TokenSession} TokenSession of reauthed token
 * @throws {Rest.js response} If reauth attempt fails
 */
export function* performReauth() {
    let state = yield select();
    const siteId = _.get(state, 'core.constants.siteId');
    const tokenBaseURL = _.get(state, 'core.constants.urls.token');
    const tokenClient = yield call(createTokenClient, siteId, tokenBaseURL);
    const tokenSession = yield call([tokenClient, tokenClient.attemptReauth]);

    return tokenSession;
}

/**
 * Given the redux state, create a new TokenClient and get a guest token
 * @return {TokenSession} TokenSession of new guest token
 * @throws {Rest.js response} If getting guest token fails
 */
export function* getGuestToken() {
    let state = yield select();
    const siteId = _.get(state, 'core.constants.siteId');
    const tokenBaseURL = _.get(state, 'core.constants.urls.token');
    const tokenClient = yield call(createTokenClient, siteId, tokenBaseURL);
    const tokenSession = yield call([tokenClient, tokenClient.getGuestToken]);

    return tokenSession;
}

/**
 * Check a users cookies and route.scope to see if they need to be reauthed and/or login.
 * @param {Object} tokenClient An object with methods for obtaining/updating tokens
 * @param {Object} authVars A config object with values representing the users current auth state
 * @param {Object} route A config object for the requested route
 * @param {Object} tokenSession TokenSession
 * @param {Integer} attempt Number of times attempted
 */
export function* handleAuth(tokenClient, authVars, route, tokenSession, attempt = 0) {
    try {
        authVars.tokenSession = yield call([tokenSession, tokenSession.fromCookie]);
        authVars.hasTokenCookie = true;
        authVars.isGuestToken = authVars.tokenSession.hasScope('GUEST');

        if (authVars.tokenSession.isNearlyExpired()) {
            // If the token is expired, or nearly expired, we need to ensure that we reauth, or get a new guest
            // session before attempting to run the route.  This goes for any scope and will ensure that scope
            // transitions, e.g. Authorized -> Registered will be handled before the scope check.
            try {
                info('Token session is nearly expired, but can be reauthed.');
                authVars.tokenSession = yield call([tokenClient, tokenClient.attemptReauth]);
            } catch(err) {
                // If a session cannot be reauthorized, usually due to account locked scenerios, or
                // password resets that have occured since the this session was obtained. We need to
                // get a guest session so the user can continue.
                error('Got a new guest session due to error on existing session');
                authVars.tokenSession = yield call([tokenClient, tokenClient.getGuestToken]);
            }
            route.session = authVars.tokenSession;
        }

        // Check for an intersection between the users session scope against the route scope
        if (route.scope) {
            if (!authVars.tokenSession.hasScope(route.scope)) {
                warn(`Token session doesn't have scope ${route.scope}`);
                authVars.showLoginPrompt = true;
            }
        }

    } catch (err) {
        if (err instanceof MissingAuthCookieError) {
            warn('Missing auth cookie, getting new guest token');
            if (attempt < 1) {
                authVars.tokenSession = yield call([tokenClient, tokenClient.getGuestToken]);
                yield call(handleAuth, tokenClient, authVars, route, TokenSession, attempt + 1);
            } else {
                error(`Retrying route after trying to get guest token failed: ${retryFailedErr}`);
                authVars.hasTokenCookie = false;
                authVars.showLoginPrompt = true;
            }
        } else {
            warn(`Unknown error in handleAuth: ${err}`);
        }
    }
}

/**
 * A helper saga to show the login prompt during auth workflow.
 * @param {Object} route REST route params
 * @param {String} route.path URL for the service
 * @param {String} route.method HTTP Verb.  GET/POST/PUT/DELETE/etc
 * @param {String} route.scope Scope required for the service call
 * @param {Boolean} route.localStorage true/false value for whether to store in localStorage
 * @param {Boolean} route.reduxCache true/false value for whether to store in redux cache
 * @param {Number} route.cacheTTL An override for the ttl values found in the response headers
 * @param {String} route.cacheKey An override for the cachekey value of the item
 */
export function* handleLogin(route) {
    if (window.location.pathname.includes('/account')) {

        let state = yield select();
        const contextPath = state.core.contextPath;

        if(route.model.constructor.name === 'ProfileModel') {
            window.location = contextPath + `login?url=${contextPath}account/account-settings`;
        } else {
            window.location = contextPath + `login?url=${contextPath}account/address`;
        }

    } else {
        const userAction = _.get(route, 'userAction', '');

        const loginInitWithUserAction = function(store, overrides = {}) {
            overrides.userAction = userAction;
            return loginInit(store, overrides);
        };
        launchModal(loginInitWithUserAction);

        // eslint-disable-next-line no-unused-vars
        const { canceled, loginFinished } = yield race({
            canceled: take(LOGIN_CANCELED),
            loginFinished: take(LOGIN_FINISHED)
        });

        if (canceled) {
            route.canceled = true;
        }

        if (route && loginFinished) {
            route.session = _.get(loginFinished, 'payload.session', {});
        }
    }

}

/**
 * If the user is presented with the login modal during a request, this will handle auth flows
 * @param {Object} tokenClient An object with methods for obtaining/updating tokens
 * @param {Object} authVars A config object with values representing the users current auth state
 * @param {Object} route A config object for the requested route
 */
export function* handleLoginRequired(tokenClient, authVars, route) {
    if (authVars.showLoginPrompt && !route.suppressLoginPrompt) {
        if (authVars.isGuestToken || !authVars.hasTokenCookie) {
            try {
                if (!authVars.hasTokenCookie) {
                    warn('Can\'t read token cookie.  Getting new guest token.');
                    authVars.tokenSession = yield call([tokenClient, tokenClient.getGuestToken]);
                }

                if (route.scope) {
                    if (!authVars.tokenSession.hasScope(route.scope)) {
                        warn(`Token session doesn't have required route scope: ${route.scope}.`);
                        warn(`Login required, guest token cannot access route ${route.path} with scope ${route.scope}.`);
                        yield call(handleLogin, route);
                    } else {
                        route.session = authVars.tokenSession;
                    }
                } else {
                    route.session = authVars.tokenSession;
                }
            } catch (err) {
                error('Unable to get new guest token!');
            }
        } else {
            info(`Login required, current token session has scopes: ${authVars.tokenSession.scopes}`);
            yield call(handleLogin, route);
        }
    }
}

/**
 * Handle user login separate from the base makeRequest flow
 * @param  {string} userAction  destination tag, used for analytics, "write-reviews", "add-to-wishlist", etc
 * @return {Object}             Handled login state
 */
export function* handleUserLogin(userAction = "") {
    let state = yield select();
    const loginState = {
        canceled: false,
        loginFinished: false
    };

    if (needsLogin(state)) {
        const loginInitWithUserAction = function(store, overrides = {}) {
            overrides.userAction = userAction;
            return loginInit(store, overrides);
        };
        launchModal(loginInitWithUserAction);

        const { canceled, loginFinished } = yield race({
            canceled: take(LOGIN_CANCELED),
            loginFinished: take(LOGIN_FINISHED)
        });

        if (canceled) {
            loginState.canceled = true;
        }

        if (loginFinished) {
            loginState.loginFinished = true;
        }
    } else {
        loginState.loginFinished = true;
    }

    yield put(userLoginHandled(loginState));
    return loginState;
}
